import React from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import { Loader2, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface MutationButtonProps extends Omit<ButtonProps, 'children'> {
  isLoading?: boolean;
  loadingText?: string;
  icon?: LucideIcon;
  children: React.ReactNode;
  glassMorphism?: boolean;
}

export function MutationButton({
  isLoading = false,
  loadingText,
  icon: Icon,
  children,
  disabled,
  className,
  variant = "default",
  glassMorphism = true,
  ...props
}: MutationButtonProps) {
  const isDisabled = disabled || isLoading;
  
  // Apply glass morphism styles for primary buttons
  const glassStyles = glassMorphism && variant === "default" ? {
    className: cn(
      "bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90",
      "border border-blue-500/30 shadow-lg transition-all duration-300",
      className
    ),
    style: {
      boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
    }
  } : {
    className,
    style: undefined
  };

  return (
    <Button
      disabled={isDisabled}
      variant={variant}
      {...glassStyles}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {loadingText || children}
        </>
      ) : (
        <>
          {Icon && <Icon className="mr-2 h-4 w-4" />}
          {children}
        </>
      )}
    </Button>
  );
}